/**
 * PlatformNoticeBanner — Elegant Platform Information Banner
 *
 * Premium banner with golden ratio spacing, liquid glass effects, and smooth animations.
 * Displays platform information on the authentication page only.
 *
 * Design Canon: tokens-only, WCAG 2.2 AA, 240ms motion, golden ratio (φ = 1.618)
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SFIcon } from "@/components/ui/sf-icon";
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { sfIcons } from "@/lib/sf-icons";
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'platform-notice-banner-dismissed-session';

export function BetaComplianceBanner() {
  const { t } = useTranslation('common');
  const prefersReducedMotion = useReducedMotion();
  const [dismissed, setDismissed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Check if production environment (SSR-safe)
  useEffect(() => {
    // SSR safety check
    if (typeof window === 'undefined') {
      setIsInitialized(true);
      return;
    }

    try {
      const hostname = window.location.hostname;
      const isProduction =
        hostname === 'supabank.xyz' ||
        hostname === 'www.supabank.xyz';
      const isLocalhost =
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.startsWith('localhost') ||
        hostname.startsWith('127.0.0.1');

      // Show on production and localhost for testing
      if (!isProduction && !isLocalhost) {
        setDismissed(true);
        setIsInitialized(true);
        return;
      }

      // Check if already dismissed this session (SSR-safe)
      try {
        const wasDismissed = sessionStorage.getItem(DISMISS_KEY);
        if (wasDismissed) {
          setDismissed(true);
        }
      } catch (e) {
        // sessionStorage not available (private mode, etc.)
        console.warn('[BetaComplianceBanner] sessionStorage not available:', e);
      }

      setIsInitialized(true);
    } catch (error) {
      console.error('[BetaComplianceBanner] Error initializing:', error);
      // Fail safe: show banner if we can't determine environment
      setIsInitialized(true);
    }
  }, []);

  // Don't render until we've checked the environment
  if (!isInitialized) {
    return null;
  }

  const handleDismiss = () => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem(DISMISS_KEY, 'true');
      }
    } catch (e) {
      console.warn('[BetaComplianceBanner] Failed to save dismiss state:', e);
    }
    setDismissed(true);
  };

  return (
    <AnimatePresence mode="wait">
      {!dismissed && (
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: -20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={prefersReducedMotion ? {} : { opacity: 0, y: -10, scale: 0.95 }}
          transition={{
            duration: prefersReducedMotion ? 0.01 : 0.24, // canonical 240ms
            ease: [0.25, 1, 0.5, 1], // ease-standard
          }}
          className={cn(semanticTokens.margin.bottom.md)}
        >
          <Alert
            className={cn(
              'group relative overflow-hidden',
              // Liquid Glass effect with gradient overlay
              'bg-gradient-to-br from-blue-50/90 via-indigo-50/80 to-purple-50/70',
              'dark:from-blue-950/40 dark:via-indigo-950/30 dark:to-purple-950/20',
              'backdrop-blur-xl backdrop-saturate-[180%]',
              // Border with golden ratio spacing
              'border-2 border-blue-200/60 dark:border-blue-800/40',
              // Shadow with depth
              'shadow-[0_8px_32px_rgba(59,130,246,0.15)] dark:shadow-[0_8px_32px_rgba(30,58,138,0.25)]',
              // Golden ratio padding (φ-based spacing)
              'p-[1.618rem]', // ≈ 26px (golden ratio)
              'rounded-2xl',
              // Typography with golden ratio line-height
              semanticTokens.motion.standard,
              // Hover effect (subtle scale)
              !prefersReducedMotion && 'hover:shadow-[0_12px_40px_rgba(59,130,246,0.2)] transition-shadow duration-240 ease-standard',
            )}
            role="status"
            aria-live="polite"
          >
            {/* Gradient overlay for depth */}
            <div
              className={cn(
                'absolute inset-0 opacity-30 pointer-events-none',
                'bg-gradient-to-br from-blue-400/10 via-transparent to-purple-400/10',
                'dark:from-blue-500/5 dark:via-transparent dark:to-purple-500/5',
              )}
              aria-hidden="true"
            />

            {/* Content with golden ratio spacing */}
            <div className="relative z-10">
              {/* Icon and Title row with golden ratio gap */}
              <div className={cn('flex items-start gap-[1.618rem]')}>
                <motion.div
                  initial={prefersReducedMotion ? {} : { scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    duration: prefersReducedMotion ? 0.01 : 0.36, // slightly slower for icon
                    delay: prefersReducedMotion ? 0 : 0.12,
                    ease: [0.34, 1.56, 0.64, 1], // elastic ease for playful entrance
                  }}
                  className="flex-shrink-0"
                >
                  <div
                    className={cn(
                      'relative',
                      'w-12 h-12', // Golden ratio size (12 * φ ≈ 19.4, rounded to 12*4 = 48px)
                      'rounded-xl',
                      'bg-gradient-to-br from-blue-500 to-indigo-600',
                      'dark:from-blue-600 dark:to-indigo-700',
                      'shadow-lg shadow-blue-500/25',
                      'flex items-center justify-center',
                      // Subtle pulse animation (only if motion allowed)
                      !prefersReducedMotion && 'animate-pulse',
                    )}
                    style={{
                      animationDuration: '3s', // Slower pulse for elegance
                    }}
                  >
                    <SFIcon
                      icon={sfIcons.badge}
                      className={cn(
                        'h-6 w-6 text-white',
                        'drop-shadow-sm',
                      )}
                      aria-hidden="true"
                    />
                    {/* Glow effect */}
                    <div
                      className={cn(
                        'absolute inset-0 rounded-xl',
                        'bg-gradient-to-br from-white/20 to-transparent',
                        'blur-sm',
                      )}
                      aria-hidden="true"
                    />
                  </div>
                </motion.div>

                {/* Title and Description with golden ratio spacing */}
                <div className={cn('flex-1 min-w-0 space-y-[1rem]')}>
                  <AlertTitle
                    className={cn(
                      semanticTokens.typography.h4,
                      'font-display font-semibold',
                      'text-blue-900 dark:text-blue-100',
                      'leading-tight', // Tight line-height for elegance
                    )}
                  >
                    {t('platform_notice.title', 'Platform Notice (Beta)')}
                  </AlertTitle>

                  <AlertDescription
                    className={cn(
                      semanticTokens.typography.body,
                      'text-blue-800/90 dark:text-blue-200/90',
                      'leading-relaxed', // Relaxed line-height (1.6) for readability
                      'max-w-3xl', // Optimal reading width (golden ratio based)
                    )}
                  >
                    {t('platform_notice.description', 'Supabank is continuously refined to deliver the best experience. Starting with smaller amounts is recommended while the platform evolves. All funds are secured in custodial ledgers with full transparency and bank-grade protection.')}
                  </AlertDescription>
                </div>
              </div>
            </div>

            {/* Dismiss button - elegant positioning with golden ratio */}
            <motion.button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDismiss();
              }}
              initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: prefersReducedMotion ? 0.01 : 0.24,
                delay: prefersReducedMotion ? 0 : 0.18,
              }}
              className={cn(
                // Position with golden ratio spacing from edges
                'absolute top-[1.618rem] right-[1.618rem]',
                // Size with golden ratio
                'w-8 h-8', // 8 * φ ≈ 13px, rounded to 32px (2 * 16px)
                'rounded-lg',
                // Background with glass effect
                'bg-white/60 dark:bg-slate-800/60',
                'backdrop-blur-sm',
                'border border-blue-200/40 dark:border-blue-700/40',
                // Hover states
                'hover:bg-white/80 dark:hover:bg-slate-700/80',
                'hover:scale-110',
                'hover:shadow-md',
                // Focus ring
                'focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2',
                // Transitions
                semanticTokens.motion.fast,
                'transition-all',
                // Ensure button is clickable
                'z-50 cursor-pointer',
              )}
              aria-label={t('platform_notice.dismiss', 'Got it')}
            >
              <SFIcon
                icon={sfIcons.close}
                className={cn(
                  'h-4 w-4',
                  'text-blue-600 dark:text-blue-400',
                  'mx-auto',
                )}
                aria-hidden="true"
              />
            </motion.button>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
